import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useAdmin } from '../../context/admin';
import { useAuth } from '../../context/auth';
import { useQueryNav } from '../../lib/nav';
import { ApiError, kbApi } from '../../lib/api';
import type { KbArticle, KbOwner } from '../../lib/types';
import { SkeletonLines } from '../ui/Skeleton';

/**
 * One shelf of knowledge: its articles on the left, the open one on the right.
 *
 * The same editor serves all three shelves — the general one on the Knowledge
 * Base page, a company's and a well's on the company's page — because an
 * article is the same thing on each; only whose it is differs, and that is the
 * `owner`. Give the component a `key` made from the owner where the owner can
 * change, so one shelf's draft never shows up on another.
 *
 * Whether the person may edit comes from the server with the listing. Hiding
 * the buttons is a courtesy; the API checks again on every save.
 */

interface Props {
  owner: KbOwner;
  /** Said when nothing is open and the shelf can be edited. */
  hint: string;
  /** Example title in the empty editor. */
  titlePlaceholder?: string;
  /** Example body in the empty editor. */
  bodyPlaceholder?: string;
  /** Told the shelf's articles whenever they are read. */
  onLoaded?: (articles: KbArticle[]) => void;
}

function errorText(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

export function ArticleShelf({ owner, hint, titlePlaceholder, bodyPlaceholder, onLoaded }: Props) {
  const { params, set } = useQueryNav();
  const { access } = useAdmin();
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const [articles, setArticles] = useState<KbArticle[] | null>(null);
  const [mayEdit, setMayEdit] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // While a superadmin looks at the portal as somebody else, the shelf is
  // shown as that person would read it.
  const editable = mayEdit && !access.preview;

  const selectedId = Number(params.get('article')) || 0;
  const selected = useMemo(
    () => articles?.find((a) => a.id === selectedId) ?? null,
    [articles, selectedId],
  );

  const kind = owner.kind;
  const ownerId = owner.kind === 'general' ? 0 : owner.id;

  const load = useCallback(() => {
    const which: KbOwner = kind === 'general' ? { kind } : { kind, id: ownerId };

    kbApi
      .shelf(which)
      .then((shelf) => {
        setArticles(shelf.items);
        // An API from before shelves existed does not say; there the general
        // shelf was the superadmin's to edit, and it was the only shelf.
        setMayEdit(shelf.editable ?? (kind === 'general' && isSuperadmin));
        onLoaded?.(shelf.items);
      })
      .catch((err) => {
        setArticles([]);
        setMayEdit(false);
        onLoaded?.([]);
        setError(errorText(err, 'Could not load the articles.'));
      });
    // onLoaded is the caller's to keep stable; a new function each render must
    // not refetch the shelf.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, ownerId, isSuperadmin]);

  useEffect(load, [load]);

  // Which article the editor was last filled with. Saving re-reads the shelf,
  // and the article just saved comes back as a new object: without this the
  // editor would be "opened" again by its own save, and the line saying it
  // was saved would be cleared the moment it appeared.
  const filled = useRef<number | null>(null);

  // Opening an article — from the list, or from a citation in the chat —
  // fills the editor with it.
  useEffect(() => {
    if (!selected || filled.current === selected.id) return;

    filled.current = selected.id;
    setCreating(false);
    setTitle(selected.title);
    setBody(selected.body);
    setConfirmDelete(false);
    setNotice(null);
  }, [selected]);

  const pick = (id: number) => {
    setError(null);
    set({ article: String(id) });
  };

  const startNew = () => {
    setError(null);
    setNotice(null);
    setCreating(true);
    setTitle('');
    setBody('');
    filled.current = null;
    set({ article: null });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    setNotice(null);

    try {
      const saved = creating
        ? await kbApi.createArticle({ title, body }, owner)
        : await kbApi.updateArticle(selectedId, { title, body });

      setNotice(
        saved.embedded
          ? 'Saved. The assistant can use it from the next question.'
          : 'Saved, but the model provider did not respond — the assistant will pick it up after the API restarts.',
      );
      setCreating(false);
      // What is in the editor is what was saved, as the server tidied it.
      filled.current = saved.id;
      setTitle(saved.title);
      setBody(saved.body);
      load();
      set({ article: String(saved.id) }, { replace: true });
    } catch (err) {
      setError(errorText(err, 'Could not save the article.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!selected || busy) return;

    setBusy(true);
    try {
      await kbApi.deleteArticle(selected.id);
      setConfirmDelete(false);
      filled.current = null;
      set({ article: null }, { replace: true });
      setTitle('');
      setBody('');
      load();
    } catch (err) {
      setError(errorText(err, 'Could not delete the article.'));
    } finally {
      setBusy(false);
    }
  };

  const showEditor = creating || selected !== null;

  return (
    <section className="kbpanel kbsplit">
      <aside className="kblist" aria-label="Articles">
        {editable && (
          <button type="button" className="kbbtn kbbtn--primary kbnew" onClick={startNew}>
            + New article
          </button>
        )}

        {articles === null && <SkeletonLines lines={6} label="Loading articles" />}
        {articles?.length === 0 && !error && <p className="content-loading">No articles yet.</p>}

        {articles?.map((a) => (
          <button
            key={a.id}
            type="button"
            className={a.id === selectedId ? 'kbitem is-selected' : 'kbitem'}
            onClick={() => pick(a.id)}
          >
            <span className="kbitem-title">{a.title}</span>
            <span className="kbitem-meta">
              {a.embedded ? 'Ready for the assistant' : 'Not yet readable by the assistant'}
            </span>
          </button>
        ))}
      </aside>

      <div className="kbeditor">
        {error && (
          <div className="alert alert--page" role="alert">
            {error}
          </div>
        )}
        {notice && <p className="kbnotice">{notice}</p>}

        {!showEditor && !error && (
          <p className="content-loading">
            {articles === null
              ? ''
              : editable
                ? hint
                : articles.length > 0
                  ? 'Pick an article to read it.'
                  : 'Nothing has been written here yet.'}
          </p>
        )}

        {showEditor && (
          <form className="kbform kbform--article" onSubmit={save}>
            <label>
              Title
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={titlePlaceholder ?? 'How NPT is coded on this contract'}
                maxLength={200}
                required
                readOnly={!editable}
                autoFocus={creating}
              />
            </label>

            <label className="kbbody">
              Article
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={
                  bodyPlaceholder ?? 'Write it as you would explain it to a new engineer on the rig.'
                }
                maxLength={20000}
                required
                readOnly={!editable}
              />
              <small className="kbmuted">{body.length.toLocaleString()} / 20,000 characters</small>
            </label>

            {editable && (
              <div className="kbform-actions">
                <button type="submit" className="kbbtn kbbtn--primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>

                <span className="kbspacer" />

                {!creating &&
                  selected &&
                  (confirmDelete ? (
                    <>
                      <span className="kbconfirm">Delete “{selected.title}”?</span>
                      <button
                        type="button"
                        className="kbbtn kbbtn--danger"
                        onClick={() => void remove()}
                        disabled={busy}
                      >
                        Delete
                      </button>
                      <button type="button" className="kbbtn" onClick={() => setConfirmDelete(false)}>
                        Keep
                      </button>
                    </>
                  ) : (
                    <button type="button" className="kbbtn kbbtn--danger" onClick={() => setConfirmDelete(true)}>
                      Delete…
                    </button>
                  ))}
              </div>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
